import React from 'react';
import ReactDOM from 'react-dom';
import Promise from 'bluebird';

import { BaseView } from '@bingads-webui/base-view';
import { downloadify } from '@bingads-webui/download-service';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';

import { AudienceDetailPanel } from './detail-panel/detail-panel';
import { ResultPanel } from './result-panel/result-panel';

import {
  uploadFileType,
  actionOptions,
  hashOptions,
  panelStateOptions,
  progressStateOptions,
} from './common/constants';

const AudienceI18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/audiences');
const PageContext = globalContext.require('PageContext');
const UploadService = globalContext.require('component/azure-upload/index');

export class CustomerListAudiencePanelView extends BaseView {
  initialize({
    isEditMode = false,
    isPopup = false,
    detailPanelAttachPoint = null,
    resultPanelAttachPoint = null,

    saveCustomerListAudienceAsync,
    setParentDetailPanelVisible,
    setParentResultPanelVisible,
    setIsUploadNeeded,
    redirectToSharedLibrary,
    setPanelStepChangeEnabled = () => {},
    perfMarker = null,
  }) {
    this.isEditMode = isEditMode;
    this.isPopup = isPopup;

    this.detailPanelAttachPoint = detailPanelAttachPoint;
    this.resultPanelAttachPoint = resultPanelAttachPoint;

    this.saveCustomerListAudienceAsync = saveCustomerListAudienceAsync;
    this.setParentDetailPanelVisible = setParentDetailPanelVisible;
    this.setParentResultPanelVisible = setParentResultPanelVisible;
    this.setIsUploadNeeded = setIsUploadNeeded;
    this.redirectToSharedLibrary = redirectToSharedLibrary;
    this.setPanelStepChangeEnabled = setPanelStepChangeEnabled;
    this.perfMarker = perfMarker;

    this.detailPanel = null;
    this.resultPanel = null;

    // Information of sub panels
    this.isActionRequired = false;
    this.actionSelection = actionOptions.noop;
    this.hashSelection = hashOptions.plainText;
    this.customerListFile = null;
    this.consentStatus = false;

    // Used when toggle require action in the edit mode
    this.setIsUploadNeeded(!isEditMode);
  }

  // Render & remove for hildren panels
  renderDetailPanel = () => {
    ReactDOM.render(
      (
        <AudienceDetailPanel
          i18n={AudienceI18n}
          isEditMode={this.isEditMode}
          onActionRequirementChanged={this.onActionRequirementChanged}
          onActionSelectionChanged={this.onActionSelectionChanged}
          onFileChangedAsync={this.onFileChangedAsync}
          onHashSelectionChanged={this.onHashSelectionChanged}
          permissions={PageContext.Permissions}
          culture={PageContext.Culture}
          templatePath={PageContext.TemplatesPathUrl}
          ref={(el) => { this.detailPanel = el; }}
        />
      ),
      this.detailPanelAttachPoint
    );
  }

  removeDetailPanel = () => {
    if (this.detailPanelAttachPoint) {
      ReactDOM.unmountComponentAtNode(this.detailPanelAttachPoint);
    }
  }

  renderResultPanel = () => {
    ReactDOM.render(
      (
        <ResultPanel
          i18n={AudienceI18n}
          panelState={panelStateOptions.idle}
          progressState={progressStateOptions.idle}
          onApplyChangeButtonClicked={this.onApplyChange}
          onDownloadResultButtonClicked={this.onDownloadValidationResult}
          onCancelButtonClicked={this.redirectToSharedLibrary}
          onDoneButtonClicked={this.redirectToSharedLibrary}
          ref={(el) => { this.resultPanel = el; }}
        />
      ),
      this.resultPanelAttachPoint
    );
  }

  removeResultPanel = () => {
    if (this.resultPanelAttachPoint) {
      ReactDOM.unmountComponentAtNode(this.resultPanelAttachPoint);
    }
  }

  // Detailed panel related functions
  onActionRequirementChanged = (isActionRequired) => {
    this.isActionRequired = isActionRequired;
    this.setIsUploadNeeded(isActionRequired);
  }

  onActionSelectionChanged = (selection) => { this.actionSelection = selection; }

  onHashSelectionChanged = (selection) => { this.hashSelection = selection; }

  onFileChangedAsync = (file) => { this.customerListFile = file; }

  onFinishAudienceDetail = () => {
    // finish detail panel and go to update
    if (!this.detailPanel.validate()) {
      return Promise.resolve();
    }

    return this.processValidation();
  }

  // Upload panel related functions
  generateCustomerListAudienceModel = (actionType, fileIdentifier) => ({
    CustomerListActionType: actionType,
    CustomerListFileIdentifier: fileIdentifier,
  });

  processValidation = () => {
    this.setParentDetailPanelVisible(false);
    this.setParentResultPanelVisible(true);


    // Parse file data rows
    const validationProcesser = new Promise((resolve) => {
      this.resultPanel.setPanelState(panelStateOptions.validateFile);
      this.resultPanel.setProgressState(progressStateOptions.validateRows);

      this.customerListFile.parseDataRows();
      resolve();
    })
      .then(() => {
        // Generate validation result file
        this.resultPanel.setProgressState(progressStateOptions.generateResultFile);

        this.customerListFile.generateValidationResult();
      })
      .then(() => {
        this.resultPanel.setProgressState(progressStateOptions.finished);
      })
      .then(() => {
        const validationResultMeta = this.customerListFile.getValidationResultMeta();

        this.resultPanel.setResultTableData(
          validationResultMeta.goodRowsNum,
          validationResultMeta.errorRowsNum
        );

        this.resultPanel.setPanelState(panelStateOptions.previewResult);
        this.resultPanel.setProgressState(progressStateOptions.idle);
      });

    return validationProcesser;
  }

  onDownloadValidationResult = () => { downloadify('validation-result.csv', this.customerListFile.getValidationResultFile(), { type: 'text/plain' }); }

  onApplyChange = () => { this.processSavingAudience(); }

  processSavingAudience = () => {
    this.resultPanel.setPanelState(panelStateOptions.uploadFile);
    let blobName = '';

    const savingAudienceProcessor = new Promise((resolve) => {
      this.resultPanel.setProgressState(progressStateOptions.generateUploadFile);


      setTimeout(() => {
        this.customerListFile.generateUploadFileAsync(this.perfMarker)
          .then(resolve);
      });
    })
      .then(() => {
        this.resultPanel.setProgressState(progressStateOptions.uploadFile);
        this.setPanelStepChangeEnabled(false);

        const fileToUpload = this.customerListFile.getUploadFile();
        fileToUpload.name = 'CustomerListFile.csv';

        const uploadService = new UploadService({
          file: fileToUpload,
          bulkFileType: uploadFileType,
        });

        return uploadService.start().then((blobUrl) => {
          blobName = (blobUrl.split('?')[0]).split('/').pop();
        });
      })
      .then(() => {
        this.resultPanel.setProgressState(progressStateOptions.createAudience);

        const customerListAudienceModel =
          this.generateCustomerListAudienceModel(this.actionSelection.type, blobName);

        return this.saveCustomerListAudienceAsync(customerListAudienceModel);
      })
      .then(() => {
        this.resultPanel.setProgressState(progressStateOptions.finished);
        this.resultPanel.setPanelState(panelStateOptions.success);
      })
      .catch(() => {
        // When encounter error, go back to preview
        this.resultPanel.setPanelState(panelStateOptions.previewResult);
        this.resultPanel.setProgressState(progressStateOptions.idle);
        this.setPanelStepChangeEnabled(true);
      });

    return savingAudienceProcessor;
  }

  render = () => {
    this.renderDetailPanel();
    this.renderResultPanel();

    return this;
  }

  remove = () => {
    this.removeDetailPanel();
    this.removeResultPanel();

    return super.remove();
  }
}
